/**
 * Olo Borderless Configuration
 * 
 * Centralizes all Olo Borderless Authentication configuration and logic
 * for use in login, checkout, and other components.
 */

/**
 * Detects the current environment based on URL and environment variables
 * @returns {Object} Environment information
 */
export function getEnvironment() {
  if (typeof window === 'undefined') {
    return {
      type: 'unknown',
      isCatering: false,
      origin: '',
      hostname: ''
    }
  }

  const hostname = window.location.hostname
  const origin = window.location.origin
  const href = window.location.href

  // Detect if it's catering based on multiple criteria
  const isCatering = 
    hostname.includes('catering') ||
    href.includes('catering') ||
    href.includes(':2001') // Local catering port

  // Detect environment type
  let type = 'production'
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    type = 'local'
  } else if (hostname.includes('sandbox')) {
    // Includes: sandbox--, cateringsandbox--
    type = 'sandbox'
  } else if (hostname.includes('stage') || hostname.startsWith('catering--')) {
    // Stage includes: stage--, catering-- (NOT cateringsandbox--)
    type = 'stage'
  } else if (hostname.includes('netlify.app')) {
    type = 'preview'
  }

  return {
    type,
    isCatering,
    origin,
    hostname,
    isLocal: type === 'local',
    isSandbox: type === 'sandbox',
    isStage: type === 'stage',
    isProduction: type === 'production'
  }
}

/**
 * Returns the Olo API URL based on the environment
 * @returns {string} Olo API URL
 */
export function getOloApiUrl() {
  const env = getEnvironment()

  // Catering may have its own API URL
  if (env.isCatering && process.env.OLO_API_URL_CATERING) {
    return process.env.OLO_API_URL_CATERING
  }

  // Prioritize environment variable if it exists
  if (process.env.OLO_API_URL) {
    return process.env.OLO_API_URL
  }

  // Local and sandbox use Olo sandbox
  if (env.isLocal || env.isSandbox) {
    return 'https://app.oloauthsandbox.com/api'
  }

  // Stage, production, and preview use Olo production
  return 'https://app.oloauth.com/api'
}

/**
 * Returns the Olo Channel ID based on the environment
 * @returns {string|null} Olo Channel ID
 */
export function getOloChannelId() {
  const env = getEnvironment()

  // Catering has its own Channel IDs
  if (env.isCatering) {
    const channelId = process.env.OLO_CHANNEL_ID_CATERING || null
    console.log('[OloConfig] getOloChannelId - Catering detected:', {
      channelId: channelId ? 'SET' : 'NOT SET',
      hostname: env.hostname
    })
    return channelId
  }

  // Mealtime
  const channelId = process.env.OLO_CHANNEL_ID || null
  console.log('[OloConfig] getOloChannelId - Mealtime:', {
    channelId: channelId ? 'SET' : 'NOT SET',
    hostname: env.hostname
  })
  return channelId
}

/**
 * Returns the redirect URI for external signup (Vue page)
 * @returns {string} Redirect URI for signup
 */
export function getOloSignupRedirectUri() {
  const env = getEnvironment()

  // Catering
  if (env.isCatering) {
    return process.env.OLO_CATERING_REDIRECT_URI || 
           process.env.OLO_REDIRECT_URI_CATERING || 
           process.env.OLO_REDIRECT_URI ||
           `${env.origin}/account/oauthcallback`
  }

  // Mealtime
  return process.env.OLO_REDIRECT_URI || `${env.origin}/account/oauthcallback`
}

/**
 * Returns the redirect URI for overlay (Netlify Function)
 * @returns {string} Redirect URI for overlay
 */
export function getOloOverlayRedirectUri() {
  const env = getEnvironment()

  // Catering
  if (env.isCatering) {
    return process.env.OLO_REDIRECT_URI_OVERLAY_CATERING || 
           process.env.OLO_REDIRECT_URI_OVERLAY ||
           `${env.origin}/.netlify/functions/oauthcallback`
  }

  // Mealtime
  return process.env.OLO_REDIRECT_URI_OVERLAY || 
         `${env.origin}/.netlify/functions/oauthcallback`
}

/**
 * Returns the complete Olo Overlay configuration
 * @returns {Object} Overlay configuration
 */
export function getOloOverlayConfig() {
  const env = getEnvironment()
  const apiUrl = getOloApiUrl()
  const channelId = getOloChannelId()
  const overlayRedirectUri = getOloOverlayRedirectUri()

  return {
    apiUrl,
    channelId,
    redirectUri: overlayRedirectUri,
    loginUrl: `${overlayRedirectUri}?overlay=1`,
    environment: env
  }
}

/**
 * Returns the redirect URL for external signup/login
 * @param {string} type - 'login' or 'signup'
 * @returns {string} Redirect URL
 */
export function getOloExternalAuthUrl(type = 'login') {
  const apiUrl = getOloApiUrl()
  const channelId = getOloChannelId()
  const redirectUri = getOloSignupRedirectUri()

  if (!channelId) {
    console.error('[OloConfig] Channel ID not configured')
    return null
  }

  // For external signup, Borderless automatically detects that user doesn't exist
  // We don't need to add &mode=SignUp
  const redirect = `${apiUrl}/oauth/authorize?response_type=code&client_id=${channelId}&redirect_uri=${encodeURIComponent(redirectUri)}`

  return redirect
}

/**
 * Validates if the Olo configuration is complete
 * @returns {Object} Validation result
 */
export function validateOloConfig() {
  const config = getOloOverlayConfig()
  const env = config.environment
  const errors = []
  const warnings = []

  if (!config.channelId) {
    const expectedVar = env.isCatering ? 'OLO_CHANNEL_ID_CATERING' : 'OLO_CHANNEL_ID'
    errors.push(`${expectedVar} not configured`)
  }

  if (!config.apiUrl) {
    errors.push('OLO_API_URL not configured')
  }

  if (!config.redirectUri) {
    const expectedVar = env.isCatering ? 'OLO_REDIRECT_URI_OVERLAY_CATERING' : 'OLO_REDIRECT_URI_OVERLAY'
    warnings.push(`${expectedVar} not configured, using fallback`)
  }

  if ((env.isLocal || env.isSandbox) && config.apiUrl.includes('app.oloauth.com')) {
    warnings.push('Dev/sandbox environment using production API')
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    config
  }
}

/**
 * Masks sensitive credentials for secure logging
 * @param {string} value - The value to mask
 * @returns {string} Masked value or 'not set' message
 */
function maskSensitiveValue(value) {
  if (!value || value === 'null' || value === 'undefined') {
    return '❌ Not set'
  }
  
  // Show first 8 and last 4 characters, mask the middle
  if (value.length <= 12) {
    return value.substring(0, 4) + '****' + value.substring(value.length - 4)
  }
  
  return value.substring(0, 8) + '****' + value.substring(value.length - 4)
}

/**
 * Logs the current Olo configuration (for debugging)
 * @param {string} context - Call context (e.g., 'Login', 'Checkout')
 */
export function logOloConfig(context = 'Unknown') {
  const validation = validateOloConfig()
  const { config, errors, warnings } = validation

  console.group(`[OloConfig] ${context}`)
  console.log('Environment:', {
    type: config.environment.type,
    isCatering: config.environment.isCatering,
    hostname: config.environment.hostname
  })
  console.log('API URL:', config.apiUrl || '❌ Not set')
  console.log('Channel ID:', maskSensitiveValue(config.channelId))
  console.log('Overlay Redirect URI:', config.redirectUri || '❌ Not set')
  console.log('Login URL:', config.loginUrl || '❌ Not set')
  
  // Log Gateway ID if present (masked)
  const env = config.environment
  if (env.isCatering && process.env.GW_ID_CATERING) {
    console.log('Gateway ID (Catering):', maskSensitiveValue(process.env.GW_ID_CATERING))
  } else if (process.env.GW_ID) {
    console.log('Gateway ID:', maskSensitiveValue(process.env.GW_ID))
  }
  
  if (errors.length > 0) {
    console.error('❌ Errors:', errors)
  }
  
  if (warnings.length > 0) {
    console.warn('⚠️ Warnings:', warnings)
  }

  if (validation.isValid) {
    console.log('✅ Configuration valid')
  }
  
  console.groupEnd()

  return validation
}

// Nuxt.js Plugin
export default ({ app }, inject) => {
  // Inject functions into Nuxt context
  inject('oloConfig', {
    getEnvironment,
    getOloApiUrl,
    getOloChannelId,
    getOloSignupRedirectUri,
    getOloOverlayRedirectUri,
    getOloOverlayConfig,
    getOloExternalAuthUrl,
    validateOloConfig,
    logOloConfig
  })
}
